import React, { useMemo, useState } from "react";
import { format, isSameDay } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, FileArchive, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useCustomer } from "@/context/CustomerContext";
import DatasetsTable from "@/components/customer/DatasetsTable";
import PanIndiaFilter from "@/components/customer/PanIndiaFilter";
import ExpiredScreen from "@/components/customer/ExpiredScreen";
import { Button } from "@/components/ui/button";

export default function Database() {
  const { loading, active, datasets, subscription, isPanIndia, filterOpts } = useCustomer();
  const [fState, setFState] = useState("");
  const [fCity, setFCity] = useState("");
  const [fDate, setFDate] = useState(undefined);
  const [fType, setFType] = useState("all");
  const [q, setQ] = useState("");

  const today = useMemo(() => new Date(), []);
  const items = useMemo(() => {
    return (datasets || []).filter((d) => {
      if (fType !== "all" && d.type !== fType) return false;
      if (fDate && !isSameDay(new Date(d.upload_date), fDate)) return false;
      if (q && !(d.display_name || d.filename || "").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [datasets, fType, fDate, q]);

  const groupedByDay = useMemo(() => {
    return items.reduce((acc, d) => {
      const key = format(new Date(d.upload_date), "yyyy-MM-dd");
      (acc[key] ||= []).push(d);
      return acc;
    }, {});
  }, [items]);
  const dayKeys = Object.keys(groupedByDay).sort((a, b) => b.localeCompare(a));
  const todayKey = format(today, "yyyy-MM-dd");

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!active) return <ExpiredScreen />;

  const total = items.reduce((a, b) => a + (b.visible_count || 0), 0);

  return (
    <div className="animate-fade-up">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-primary/10 text-primary grid place-items-center">
              <FileArchive className="h-4 w-4" />
            </div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl">Database</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1 num">
            Full archive of every file within your subscription window · {items.length} file(s) · {total.toLocaleString()} records
          </p>
        </div>
      </div>

      <PanIndiaFilter isPanIndia={isPanIndia} filterOpts={filterOpts} fState={fState} setFState={setFState} fCity={fCity} setFCity={setFCity} />

      <Card className="p-4 mb-6" data-testid="database-filter-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Search filename</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. GST_16-09-2026" className="pl-8" data-testid="db-search" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Specific day</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="db-date-picker" className="w-full h-10 flex items-center justify-between rounded-md border border-border bg-background px-3 text-sm hover:border-primary/50 transition-colors">
                  <span className={fDate ? "num" : "text-muted-foreground"}>{fDate ? format(fDate, "dd MMM yyyy") : "Any date"}</span>
                  <span className="flex items-center gap-1">
                    {fDate && <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); setFDate(undefined); }} />}
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 bg-popover" align="start">
                <Calendar mode="single" selected={fDate} onSelect={setFDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          {subscription?.data_type === "both" && (
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={fType} onValueChange={setFType}>
                <SelectTrigger data-testid="db-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="all">GST + MCA</SelectItem>
                  <SelectItem value="gst">GST only</SelectItem>
                  <SelectItem value="mca">MCA only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setQ(""); setFDate(undefined); setFType("all"); }}
              disabled={!q && !fDate && fType === "all"}
              data-testid="db-clear"
            >
              <X className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          </div>
        </div>
      </Card>

      {items.length === 0 ? (
        <DatasetsTable datasets={[]} emptyMessage="No files match your filters" />
      ) : (
        <div className="space-y-6">
          {dayKeys.map((key) => {
            const day = new Date(key);
            const isToday = key === todayKey;
            const dayFiles = groupedByDay[key];
            const dayTotal = dayFiles.reduce((a, b) => a + (b.visible_count || 0), 0);
            return (
              <div key={key} data-testid={`day-group-${key}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-8 w-8 rounded-md bg-primary/10 text-primary grid place-items-center">
                    <CalendarIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-base flex items-center gap-2">
                      {format(day, "EEEE, dd MMM yyyy")}
                      {isToday && <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30" variant="outline">Today</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground num">{dayFiles.length} file(s) · {dayTotal.toLocaleString()} records</div>
                  </div>
                </div>
                <DatasetsTable datasets={dayFiles} isPanIndia={isPanIndia} fState={fState} fCity={fCity} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
